import type { Point, Line } from '../context/context';
import type { FixedRoute } from '../types/fixedRoutes';
import { isOsmRouteLine } from '../types/osmRoutes';

export interface TransitStopServiceSummary {
  routeId: string;
  routeLabel: string;
  modeLabel?: string;
  directionLabel?: string;
  operatorLabel?: string;
  timetableLabel: string;
  colour?: string;
  source: 'fixedRoute' | 'osm';
}

const DEFAULT_MAX_SERVICES = 8;
const DEFAULT_MAX_TIMES = 5;

function formatMinutesOfDay(totalMinutes: number): string {
  const normalized = ((Math.round(totalMinutes) % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function routeLabelForFixedRoute(route: FixedRoute): string {
  return route.shortName ? `${route.shortName} - ${route.name}` : route.name;
}

function fixedRouteTimetableLabel(
  route: FixedRoute,
  stopIndex: number,
  waitTimeMinutes: number,
  maxTimes: number
): string {
  const stopTimes = (route.tripSchedules ?? [])
    .map((trip) => {
      const offset = trip.stopArrivalTimesMinutes[stopIndex];
      return typeof offset === 'number'
        ? formatMinutesOfDay(trip.startTimeMinutes + offset)
        : null;
    })
    .filter((value): value is string => value !== null)
    .slice(0, maxTimes);

  if (stopTimes.length > 0) {
    const suffix = (route.tripSchedules?.length ?? 0) > stopTimes.length ? '...' : '';
    return `Trips: ${stopTimes.join(', ')}${suffix}`;
  }

  const pieces = [`Starts ${route.startTime}`];
  if (route.endTime) {
    pieces.push(`runs until ${route.endTime}`);
  }
  pieces.push(`wait ${waitTimeMinutes} min`);
  return pieces.join('; ');
}

export function summarizeFixedRouteStopServices(
  stopId: string,
  routes: FixedRoute[],
  options?: {
    maxServices?: number;
    maxTimes?: number;
  }
): TransitStopServiceSummary[] {
  const maxServices = options?.maxServices ?? DEFAULT_MAX_SERVICES;
  const maxTimes = options?.maxTimes ?? DEFAULT_MAX_TIMES;

  return routes
    .filter((route) => !route.hidden)
    .map((route): TransitStopServiceSummary | null => {
      const stopIndex = route.stops.findIndex((stopConfig) => stopConfig.stopId === stopId);
      if (stopIndex < 0) return null;

      const stopConfig = route.stops[stopIndex];
      const firstStopId = route.stops[0]?.stopId;
      const lastStopId = route.stops[route.stops.length - 1]?.stopId;
      const fromStopName = firstStopId === stopId ? 'this stop' : firstStopId;
      const toStopName = lastStopId === stopId ? 'this stop' : lastStopId;

      return {
        routeId: route.id,
        routeLabel: routeLabelForFixedRoute(route),
        directionLabel: firstStopId && lastStopId ? `${fromStopName} to ${toStopName}` : undefined,
        timetableLabel: fixedRouteTimetableLabel(route, stopIndex, stopConfig.waitTimeMinutes, maxTimes),
        colour: route.color,
        source: 'fixedRoute',
      };
    })
    .filter((value): value is TransitStopServiceSummary => value !== null)
    .slice(0, maxServices);
}

function osmTimetableLabel(line: Line): string {
  const meta = line.osmRouteMetadata;
  if (!meta) return 'Timetable unavailable in OSM data';

  const pieces: string[] = [];
  if (meta.interval) pieces.push(`Frequency: every ${meta.interval} min`);
  if (meta.openingHours) pieces.push(`Hours: ${meta.openingHours}`);
  if (meta.duration) pieces.push(`Duration: ${meta.duration}`);

  return pieces.length > 0 ? pieces.join('; ') : 'Timetable unavailable in OSM data';
}

function summarizeOsmRouteLine(line: Line): TransitStopServiceSummary | null {
  if (!isOsmRouteLine(line)) return null;

  const meta = line.osmRouteMetadata;
  return {
    routeId: line.id,
    routeLabel: meta.ref ? `${meta.ref} - ${meta.routeName}` : meta.routeName,
    modeLabel: meta.mode.replace('_', ' '),
    directionLabel: meta.from && meta.to ? `${meta.from} to ${meta.to}` : undefined,
    operatorLabel: meta.operator,
    timetableLabel: osmTimetableLabel(line),
    colour: line.colour || meta.colour,
    source: 'osm',
  };
}

export function summarizeOsmStopServices(
  point: Point,
  lines: Line[],
  options?: {
    maxServices?: number;
  }
): TransitStopServiceSummary[] {
  const maxServices = options?.maxServices ?? DEFAULT_MAX_SERVICES;

  return lines
    .filter(isOsmRouteLine)
    .filter((line) => line.osmRouteMetadata.stopShapeIds.includes(point.id))
    .map(summarizeOsmRouteLine)
    .filter((value): value is TransitStopServiceSummary => value !== null)
    .sort((a, b) => a.routeLabel.localeCompare(b.routeLabel))
    .slice(0, maxServices);
}

export function indexOsmStopServicesByPointId(
  lines: Line[],
  options?: {
    maxServices?: number;
  }
): Map<string, TransitStopServiceSummary[]> {
  const maxServices = options?.maxServices ?? DEFAULT_MAX_SERVICES;
  const servicesByPointId = new Map<string, TransitStopServiceSummary[]>();

  lines.forEach((line) => {
    if (!isOsmRouteLine(line)) return;
    const summary = summarizeOsmRouteLine(line);
    if (!summary) return;

    line.osmRouteMetadata.stopShapeIds.forEach((pointId) => {
      const services = servicesByPointId.get(pointId) ?? [];
      if (services.length >= maxServices) return;
      services.push(summary);
      servicesByPointId.set(pointId, services);
    });
  });

  servicesByPointId.forEach((services) => {
    services.sort((a, b) => a.routeLabel.localeCompare(b.routeLabel));
  });

  return servicesByPointId;
}
